import { useTranslation } from "react-i18next";
import { cva } from "class-variance-authority";
import { InfoIcon } from "lucide-react";
import { cn } from "~/utils";
import { Button } from "~/components/button";
import { Badge } from "~/components/ui/badge";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { Text } from "~/components/text";
import { type BundleFormat, productUrl } from "./bundle.utils";

/** Per-row entrance stagger (ms), capped so a long list never feels slow. */
const ROW_STAGGER_MS = 30;
const ROW_STAGGER_CAP_MS = 150;

const bundleRowVariants = cva(
  cn(
    "flex items-start gap-3 rounded-xl border p-3",
    "transition-[background-color,border-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]",
    "animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-both duration-200",
    "motion-reduce:animate-none motion-reduce:transition-none",
  ),
  {
    variants: {
      state: {
        current: "border-primary bg-primary-50/40",
        selected: "border-primary-200 bg-primary-50/30",
        default: "border-secondary-200 bg-white",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

interface BundleFormatRowProps {
  format: BundleFormat;
  index: number;
  isCurrent: boolean;
  isSelected: boolean;
  /** Whether the format is still active; inactive ones show a warning. */
  isActive: boolean;
  onToggle: (isbn: string) => void;
}

export const BundleFormatRow = ({
  format,
  index,
  isCurrent,
  isSelected,
  isActive,
  onToggle,
}: BundleFormatRowProps) => {
  const { t } = useTranslation(["productDetails"]);

  let state: "current" | "selected" | "default" = "default";
  if (isCurrent) {
    state = "current";
  } else if (isSelected) {
    state = "selected";
  }

  return (
    <li
      data-testid="bundle-format-row"
      style={{
        animationDelay: `${Math.min(index * ROW_STAGGER_MS, ROW_STAGGER_CAP_MS)}ms`,
      }}
      className={bundleRowVariants({ state })}
    >
      <Checkbox
        className="mt-1"
        checked={isSelected}
        disabled={isCurrent}
        onCheckedChange={() => onToggle(format.identifier)}
        aria-label={t("salesData.bundle.drawer.toggleFormat", {
          title: format.title,
          format: format.productGroupDescription,
        })}
      />
      {/* Colour swatch; dims when excluded, so the bundle reads at a glance.
          Uses the global --chart-N theme vars directly, wrapping after five. */}
      <span
        aria-hidden
        className={cn(
          "mt-1 h-5 w-5 shrink-0 rounded-xs transition-opacity duration-200 ease-out motion-reduce:transition-none",
          `bg-(--chart-${(index % 5) + 1})`,
          isSelected ? "opacity-100" : "opacity-40",
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <Text variant="label" className="truncate">
            {format.title}
          </Text>
          {!isActive && (
            <Tooltip>
              <TooltipTrigger
                aria-label={t("productDetails:productSummary.statusTooltip")}
                data-testid="bundle-format-inactive-icon"
              >
                <InfoIcon
                  className="h-4 w-4 shrink-0 text-red-500"
                  aria-hidden
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <Text variant="body">
                  {t("productDetails:productSummary.statusTooltip")}
                </Text>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Text variant="caption" className="truncate">
          {(format.author ?? []).join(", ")}
        </Text>
        <div className="mt-1 flex items-center gap-2">
          <Badge variant="secondary" className="min-w-0 shrink-0 px-2">
            {format.productGroupDescription}
          </Badge>
          <Text as="span" variant="caption" className="truncate">
            {format.identifier}
          </Text>
        </div>
      </div>
      {isCurrent ? (
        <Badge
          variant="default"
          className="mt-0.5 min-w-0 shrink-0 px-2 uppercase tracking-wide"
        >
          {t("salesData.bundle.drawer.current")}
        </Badge>
      ) : (
        <Button
          asChild
          variant="tertiary"
          size="small"
          icon="square-arrow-out-up-right"
          iconPosition="left"
          testId="bundle-format-open"
        >
          <a
            href={productUrl(format.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("salesData.bundle.drawer.open")}
          </a>
        </Button>
      )}
    </li>
  );
};
